import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import NotificationBell from './NotificationBell';
import {
  Building2, Users, CalendarCheck, LogOut, Menu, X, UserCog, Landmark, ScrollText, ListChecks, ChevronDown, TrendingUp,
} from 'lucide-react';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  organization: 'Organization',
  outlet_owner: 'Outlet Manager',
  outlet_manager: 'Outlet Manager',
};

const navItems = [
  { to: '/organizations', label: 'Organizations', icon: Landmark, roles: ['super_admin'] },
  { to: '/outlets', label: 'Outlets', icon: Building2, roles: ['super_admin', 'organization'] },
  { to: '/staff', label: 'Staff', icon: Users, roles: ['super_admin', 'organization', 'outlet_owner', 'outlet_manager'] },
  { to: '/attendance', label: 'Salary & Attendance', icon: CalendarCheck, roles: ['super_admin', 'organization', 'outlet_owner', 'outlet_manager'] },
  {
    label: 'Checklist', icon: ListChecks, roles: ['super_admin', 'organization', 'outlet_owner', 'outlet_manager'],
    children: [
      { to: '/checklist', label: 'Create Checklist' },
      { to: '/checklist-status', label: 'Assign Checklist' },
      { to: '/checklist-daily-status', label: 'Checklist Status' },
    ],
  },
  { to: '/performance', label: 'Performance', icon: TrendingUp, roles: ['super_admin', 'organization', 'outlet_owner', 'outlet_manager'] },
];

// Pinned to the bottom of the sidebar, just above the user profile
const bottomNavItems = [
  { to: '/users', label: 'Users', icon: UserCog, roles: ['super_admin'] },
  { to: '/logs', label: 'User Logs', icon: ScrollText, roles: ['super_admin'] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const allowed = navItems.filter((item) => item.roles.includes(user?.role));
  const allowedBottom = bottomNavItems.filter((item) => item.roles.includes(user?.role));

  // Submenu groups expanded by default; allow manual toggle
  const childActive = (item) => item.children?.some((c) => location.pathname.startsWith(c.to));
  const [openGroups, setOpenGroups] = useState({});
  const isGroupOpen = (item) => openGroups[item.label] ?? true;
  const toggleGroup = (item) =>
    setOpenGroups((prev) => ({ ...prev, [item.label]: !(prev[item.label] ?? true) }));

  return (
    <div className="app-layout">
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'collapsed'}`}>
        <div className="sidebar-header">
          <div className="brand">
            {sidebarOpen && <span className="brand-name">CloudStay</span>}
          </div>
          <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {allowed.map((item) => {
            const { to, label, icon: Icon, children } = item;

            if (children) {
              const open = isGroupOpen(item);
              return (
                <div key={label} className="nav-group">
                  <button
                    type="button"
                    className={`nav-item nav-group-toggle ${childActive(item) ? 'active-parent' : ''}`}
                    onClick={() => toggleGroup(item)}
                  >
                    <Icon size={20} />
                    {sidebarOpen && <span>{label}</span>}
                    {sidebarOpen && (
                      <ChevronDown size={16} className={`nav-caret ${open ? 'open' : ''}`} />
                    )}
                  </button>
                  {sidebarOpen && open && (
                    <div className="nav-submenu">
                      {children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          end
                          className={({ isActive }) => `nav-item nav-subitem ${isActive ? 'active' : ''}`}
                        >
                          <span>{c.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              >
                <Icon size={20} />
                {sidebarOpen && <span>{label}</span>}
              </NavLink>
            );
          })}

          {allowedBottom.length > 0 && (
            <div className="sidebar-nav-bottom">
              {allowedBottom.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                >
                  <Icon size={20} />
                  {sidebarOpen && <span>{label}</span>}
                </NavLink>
              ))}
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{user?.name?.[0]?.toUpperCase()}</div>
            {sidebarOpen && (
              <div className="user-details">
                <p className="user-name">{user?.name}</p>
                <p className="user-role">{ROLE_LABELS[user?.role] || user?.role?.replace('_', ' ')}</p>
              </div>
            )}
          </div>
          <button className="logout-btn" onClick={handleLogout} title="Logout">
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      <main className="main-content">
        {['super_admin', 'organization', 'outlet_owner', 'outlet_manager'].includes(user?.role) && <NotificationBell />}
        <Outlet />
      </main>
    </div>
  );
}
